import type { ReactNode } from "react";
import { Link } from "react-router";
import { ArrowLeft } from "lucide-react";
import loginBanner from "../assets/eSign_login_banner.png";
import { BrandLogo } from "./BrandLogo";

interface AuthScreenProps {
  children: ReactNode;
  bannerTitle?: ReactNode;
  bannerText?: string;
}

export function AuthScreen({
  children,
  bannerTitle = (
    <>
      <span className="block text-white">Review orders,</span>
      <span className="block text-[#ff0d6e]"> seal decisions,</span>
      <span className="block text-white"> and keep the trail intact.</span>
    </>
  ),
  bannerText = "MEATrix OrderFlow gives clinical teams one place to send, review, approve, and verify signed documents.",
}: AuthScreenProps) {
  return (
    <main className="min-h-screen min-h-[100dvh] flex flex-col lg:flex-row bg-white">
      {/* Visual Brand Banner Column */}
      <section
        className="relative hidden lg:flex lg:w-1/2 min-h-screen items-center overflow-hidden bg-cover bg-center"
        style={{ backgroundImage: `url(${loginBanner})` }}
        aria-hidden="true"
      >
        <div className="absolute inset-0 bg-gradient-to-br from-[#012699]/90 via-[#0b2981]/85 to-[#1b2e3d]/85 backdrop-blur-[1px]" />
        <div className="relative z-10 max-w-xl flex flex-col gap-6 p-12 xl:p-16 text-white">
          <div className="space-y-1">
            <h2 className="text-3xl xl:text-4xl 2xl:text-5xl font-extrabold tracking-tight leading-tight font-display">
              {bannerTitle}
            </h2>
          </div>
          <p className="text-base xl:text-lg text-white/85 leading-relaxed font-medium max-w-lg">
            {bannerText}
          </p>
        </div>
      </section>

      {/* Main Form Panel Column */}
      <section className="w-full lg:w-1/2 min-h-screen min-h-[100dvh] flex flex-col bg-white overflow-y-auto">
        <header className="min-h-[76px] flex items-center justify-between gap-4 px-6 sm:px-10 lg:px-12 pt-6 pb-3">
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#012699] hover:text-[#0b2981] transition-colors group"
            aria-label="Back to home"
          >
            <ArrowLeft size={16} className="transition-transform group-hover:-translate-x-0.5" aria-hidden="true" />
            <span>Home</span>
          </Link>
          <Link to="/" className="inline-flex items-center" aria-label="MEATrix OrderFlow home">
            <BrandLogo className="h-9 sm:h-10 w-auto object-contain" />
          </Link>
          <span className="w-12" aria-hidden="true" />
        </header>

        <div className="flex-1 flex items-center justify-center p-6 sm:p-10 lg:p-14">
          <div className="w-full max-w-md">
            {children}
          </div>
        </div>
      </section>
    </main>
  );
}
